'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, Loader2, User, Building2, Zap, ChevronLeft, ChevronRight, Shield, BarChart2, Navigation, Package, X, Globe, Moon, Sun, Upload, FileText, CreditCard, Paperclip, Phone, MessageSquare, Send, Hash } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { NexoraMark } from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import SuperAdminDashboard from '@/app/super-admin/components/SuperAdminDashboard';
import SetPasswordForm from './SetPasswordForm';

type AuthMode = 'login' | 'signup' | 'forgot';
type SignupStep = 0 | 1 | 2;
type Lang = 'ar' | 'en';
type Theme = 'light' | 'dark';

const t = {
  ar: {
    login: 'تسجيل الدخول',
    signup: 'تسجيل شركة جديدة',
    welcome: 'مرحباً بعودتك',
    welcomeSub: 'سجّل دخولك للمتابعة إلى لوحة التحكم',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    forgot: 'نسيت كلمة المرور؟',
    loginBtn: 'تسجيل الدخول',
    demoTitle: 'حسابات تجريبية — انقر للدخول مباشرة',
    demo: 'تجريبي',
    use: 'استخدم',
    superAdminLink: 'دخول لوحة تحكم المدير العام',
    forgotTitle: 'استعادة كلمة المرور',
    forgotSub: 'أدخل بريدك الإلكتروني لإرسال رابط الاستعادة',
    sendReset: 'إرسال رابط الاستعادة',
    backToLogin: 'العودة لتسجيل الدخول',
    companyInfo: 'معلومات الشركة',
    adminAccount: 'حساب المدير',
    companyInfoTitle: 'معلومات الشركة',
    companyInfoSub: 'أدخل بيانات شركتك للتسجيل في المنصة',
    companyNameAr: 'اسم الشركة بالعربي',
    companyNameEn: 'Company Name in English',
    crNumber: 'السجل التجاري',
    vatNumber: 'الرقم الضريبي',
    city: 'المدينة',
    phone: 'رقم الجوال',
    nationalAddress: 'العنوان الوطني',
    zatcaRequired: 'مطلوب لزاتكا',
    buildingNumber: 'رقم المبنى',
    streetName: 'اسم الشارع',
    district: 'الحي',
    postalCode: 'الرمز البريدي',
    additionalNumber: 'الرقم الإضافي',
    optional: '(اختياري)',
    choosePackage: 'اختر الباقة',
    next: 'التالي',
    adminTitle: 'حساب مدير الشركة',
    adminSub: 'بيانات المدير الرئيسي للشركة',
    fullName: 'الاسم الكامل',
    passwordMin: '8 أحرف على الأقل',
    confirmPassword: 'تأكيد كلمة المرور',
    back: 'رجوع',
    submit: 'تقديم الطلب',
    successTitle: 'تم تقديم الطلب بنجاح!',
    successSub: 'تم استلام طلب انضمام شركتك. سيقوم فريقنا بمراجعة الطلب وإشعارك على البريد الإلكتروني خلال 24 ساعة.',
    requestSummary: 'ملخص الطلب',
    companyLabel: 'اسم الشركة',
    emailLabel: 'البريد الإلكتروني',
    packageLabel: 'الباقة المختارة',
    backToLoginBtn: 'العودة لتسجيل الدخول',
    selectCity: 'اختر المدينة',
    chosenPackage: 'الباقة المختارة',
    change: 'تغيير',
    platformName: 'منصة إدارة الأعمال المتكاملة',
    platformSub: 'نظام ERP سحابي احترافي متكامل — متوافق مع متطلبات هيئة الزكاة والدخل والجمارك (زاتكا) المرحلة الثانية، ومعايير IFRS المحاسبية.',
    features: ['متوافق مع زاتكا المرحلة الثانية', 'دعم متعدد الفروع والشركات', 'أمان وحماية بيانات متقدمة', 'تقارير مالية وضريبية شاملة'],
    copyright: '© 2026 Nexora ERP. جميع الحقوق محفوظة.',
    superAdminPanel: 'لوحة تحكم المدير العام',
    superAdminPanelSub: 'أدخل بريد المدير العام للمتابعة',
    superAdminEmailLabel: 'البريد الإلكتروني',
    superAdminEnter: 'دخول لوحة التحكم',
    superAdminGoogleBtn: 'دخول بحساب Google',
    superAdminOr: 'أو',
    monthlyPrice: '/شهر',
    yearlyPrice: '/سنة',
    free: 'مجاني',
    users: 'مستخدم',
    branches: 'فرع',
    upTo: 'حتى',
    required: 'مطلوب',
    // Documents
    supportingDocs: 'المستندات المؤيدة',
    supportingDocsSub: 'ارفع المستندات الرسمية للتحقق من هوية شركتك',
    crDocument: 'وثيقة السجل التجاري',
    crDocumentSub: 'صورة من السجل التجاري الساري',
    vatDocument: 'شهادة التسجيل الضريبي',
    vatDocumentSub: 'شهادة تسجيل ضريبة القيمة المضافة',
    paymentProof: 'سند سداد الباقة',
    paymentProofSub: 'إيصال أو لقطة شاشة لعملية الدفع',
    uploadFile: 'اختر ملفاً',
    fileUploaded: 'تم الرفع',
    uploading: 'جاري الرفع...',
    pdfOrImage: 'PDF أو صورة (حتى 10 ميجا)',
    docsOptional: 'الزامية ورسمية وصور واضحة',
    contactUs: 'تواصل معنا',
    contactUsTitle: 'تواصل مع فريق الدعم',
    contactUsSub: 'أرسل رسالتك وسيتواصل معك فريقنا في أقرب وقت',
    contactName: 'الاسم الكامل',
    contactPhone: 'رقم الهاتف',
    contactEmail: 'البريد الإلكتروني',
    contactSubject: 'عنوان التواصل',
    contactDescription: 'الشرح والتفاصيل',
    contactAttachment: 'إرفاق مستند (اختياري)',
    contactAttachmentSub: 'PDF أو صورة (حتى 10 ميجا)',
    contactSend: 'إرسال',
    contactSending: 'جاري الإرسال...',
    contactSuccess: 'تم إرسال رسالتك بنجاح! سيتواصل معك فريقنا قريباً.',
    contactError: 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.',
    contactNamePlaceholder: 'محمد بن عبدالله',
    contactPhonePlaceholder: '05XXXXXXXX',
    contactEmailPlaceholder: 'example@email.com',
    contactSubjectPlaceholder: 'موضوع رسالتك',
    contactDescriptionPlaceholder: 'اكتب تفاصيل رسالتك هنا...',
    contactAttachFile: 'اختر ملفاً',
    contactFileUploaded: 'تم الرفع',
    contactUploading: 'جاري الرفع...',
  },
  en: {
    login: 'Sign In',
    signup: 'Register New Company',
    welcome: 'Welcome Back',
    welcomeSub: 'Sign in to continue to your dashboard',
    email: 'Email Address',
    password: 'Password',
    forgot: 'Forgot password?',
    loginBtn: 'Sign In',
    demoTitle: 'Demo Accounts — Click to login instantly',
    demo: 'Demo',
    use: 'Use',
    superAdminLink: 'Super Admin Dashboard',
    forgotTitle: 'Reset Password',
    forgotSub: 'Enter your email to receive a reset link',
    sendReset: 'Send Reset Link',
    backToLogin: 'Back to Sign In',
    companyInfo: 'Company Info',
    adminAccount: 'Admin Account',
    companyInfoTitle: 'Company Information',
    companyInfoSub: 'Enter your company details to register on the platform',
    companyNameAr: 'Company Name (Arabic)',
    companyNameEn: 'Company Name (English)',
    crNumber: 'Commercial Registration',
    vatNumber: 'VAT Number',
    city: 'City',
    phone: 'Mobile Number',
    nationalAddress: 'National Address',
    zatcaRequired: 'Required for ZATCA',
    buildingNumber: 'Building Number',
    streetName: 'Street Name',
    district: 'District',
    postalCode: 'Postal Code',
    additionalNumber: 'Additional Number',
    optional: '(Optional)',
    choosePackage: 'Choose Package',
    next: 'Next',
    adminTitle: 'Company Admin Account',
    adminSub: 'Primary administrator credentials',
    fullName: 'Full Name',
    passwordMin: 'At least 8 characters',
    confirmPassword: 'Confirm Password',
    back: 'Back',
    submit: 'Submit Request',
    successTitle: 'Request Submitted Successfully!',
    successSub: 'Your company join request has been received. Our team will review and notify you within 24 hours.',
    requestSummary: 'Request Summary',
    companyLabel: 'Company Name',
    emailLabel: 'Email Address',
    packageLabel: 'Selected Package',
    backToLoginBtn: 'Back to Sign In',
    selectCity: 'Select City',
    chosenPackage: 'Selected Package',
    change: 'Change',
    platformName: 'Integrated Business Management Platform',
    platformSub: 'Professional cloud ERP system — compliant with ZATCA Phase 2 requirements and IFRS accounting standards.',
    features: ['ZATCA Phase 2 Compliant', 'Multi-branch & Company Support', 'Advanced Security & Data Protection', 'Comprehensive Financial Reports'],
    copyright: '© 2026 Nexora ERP. All rights reserved.',
    superAdminPanel: 'Super Admin Dashboard',
    superAdminPanelSub: 'Enter super admin email to continue',
    superAdminEmailLabel: 'Email Address',
    superAdminEnter: 'Enter Dashboard',
    superAdminGoogleBtn: 'Sign in with Google',
    superAdminOr: 'or',
    monthlyPrice: '/mo',
    yearlyPrice: '/yr',
    free: 'Free',
    users: 'users',
    branches: 'branches',
    upTo: 'Up to',
    required: 'Required',
    // Documents
    supportingDocs: 'Supporting Documents',
    supportingDocsSub: 'Upload official documents to verify your company identity',
    crDocument: 'CR Certificate',
    crDocumentSub: 'Copy of valid commercial registration',
    vatDocument: 'VAT Registration Certificate',
    vatDocumentSub: 'VAT registration certificate',
    paymentProof: 'Payment Proof',
    paymentProofSub: 'Receipt or screenshot of package payment',
    uploadFile: 'Choose File',
    fileUploaded: 'Uploaded',
    uploading: 'Uploading...',
    pdfOrImage: 'PDF or image (up to 10 MB)',
    docsOptional: '(Optional — can be attached later)',
    contactUs: 'Contact Us',
    contactUsTitle: 'Contact Support Team',
    contactUsSub: 'Send us a message and our team will get back to you shortly',
    contactName: 'Full Name',
    contactPhone: 'Phone Number',
    contactEmail: 'Email Address',
    contactSubject: 'Subject',
    contactDescription: 'Description',
    contactAttachment: 'Attach Document (Optional)',
    contactAttachmentSub: 'PDF or image (up to 10 MB)',
    contactSend: 'Send',
    contactSending: 'Sending...',
    contactSuccess: 'Your message was sent successfully! Our team will contact you soon.',
    contactError: 'An error occurred while sending. Please try again.',
    contactNamePlaceholder: 'John Doe',
    contactPhonePlaceholder: '+1234567890',
    contactEmailPlaceholder: 'example@email.com',
    contactSubjectPlaceholder: 'Message subject',
    contactDescriptionPlaceholder: 'Write your message details here...',
    contactAttachFile: 'Choose File',
    contactFileUploaded: 'Uploaded',
    contactUploading: 'Uploading...',
  }
};

interface SignupData {
  companyNameAr: string; companyNameEn: string; crNumber: string; vatNumber: string;
  city: string; phone: string; buildingNumber: string; streetName: string;
  district: string; postalCode: string; additionalNumber: string;
  packageId: string; adminName: string; adminEmail: string; adminPassword: string; confirmPassword: string;
}

interface Package {
  id: string; name_ar: string; name_en: string; description_ar: string; description_en: string;
  price_monthly: number; price_yearly: number; max_users: number; max_branches: number; features: string[];
  is_addon: boolean; plan_tier: string;
}

interface UploadState {
  file: File | null;
  url: string;
  uploading: boolean;
  error: string;
}

const cities = ['الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر', 'الأحساء', 'تبوك', 'أبها', 'نجران', 'حائل', 'القصيم', 'جازان', 'الطائف', 'ينبع'];

const demoCredentials = [
  { role: 'مدير النظام', roleEn: 'System Admin', email: 'admin@nexora.sa', password: 'NexoraAdmin@2026', color: 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300' },
  { role: 'مدير المتجر', roleEn: 'Store Manager', email: 'manager@nexora.sa', password: 'NexoraStore@2026', color: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300' },
  { role: 'كاشير', roleEn: 'Cashier', email: 'cashier@nexora.sa', password: 'NexoraCash@2026', color: 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300' },
  { role: 'محاسب', roleEn: 'Accountant', email: 'accounts@nexora.sa', password: 'NexoraAcc@2026', color: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300' },
];

function StepIndicator({ steps, currentStep, mutedStyle }: { steps: string[]; currentStep: number; mutedStyle: React.CSSProperties }) {
  return (
    <div className="flex items-center gap-0 mb-5">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-colors ${i < currentStep ? 'bg-green-500 text-white' : i === currentStep ? 'text-white' : ''}`}
              style={i === currentStep ? { background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', boxShadow: '0 4px 12px rgba(15,92,92,0.3)' } : i < currentStep ? {} : { background: 'var(--auth-input-bg)', color: 'var(--auth-text-muted)' }}
            >
              {i < currentStep ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span
              className={`text-[10px] mt-1 font-bold ${i === currentStep ? 'text-[#0F5C5C]' : ''}`}
              style={i !== currentStep ? mutedStyle : {}}
            >
              {step}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-1 mb-4 transition-colors ${i < currentStep ? 'bg-green-500' : ''}`}
              style={i >= currentStep ? { background: 'var(--auth-border)' } : {}}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function FileUploadField({
  label, sublabel, uploadState, onChange, inputStyle, labelStyle, mutedStyle, isDark, accept
}: {
  label: string; sublabel: string;
  uploadState: UploadState;
  onChange: (file: File) => void;
  inputStyle: React.CSSProperties; labelStyle: React.CSSProperties; mutedStyle: React.CSSProperties;
  isDark: boolean; accept?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFile = !!uploadState.url || !!uploadState.file;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-bold" style={labelStyle}>{label}</label>
        {hasFile && !uploadState.uploading && (
          <span className="text-[10px] text-green-600 font-bold flex items-center gap-1">
            <CheckCircle size={10} /> {uploadState.url ? 'تم الرفع' : ''}
          </span>
        )}
      </div>
      <p className="text-[10px] mb-1.5" style={mutedStyle}>{sublabel}</p>
      <div
        onClick={() => !uploadState.uploading && inputRef.current?.click()}
        className={`relative flex items-center gap-2.5 h-11 rounded-xl border cursor-pointer transition-all px-3 ${uploadState.uploading ? 'opacity-70 cursor-not-allowed' : 'hover:border-[#0F5C5C]'}`}
        style={{
          ...inputStyle,
          borderColor: uploadState.url ? '#16a34a' : uploadState.file ? '#0F5C5C' : 'var(--auth-input-border)',
          background: uploadState.url ? (isDark ? 'rgba(22,163,74,0.08)' : '#f0fdf4') : 'var(--auth-input-bg)',
        }}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${uploadState.url ? 'bg-green-100' : 'bg-[rgba(15,92,92,0.1)]'}`}>
          {uploadState.uploading ? (
            <Loader2 size={13} className="animate-spin text-[#0F5C5C]" />
          ) : uploadState.url ? (
            <CheckCircle size={13} className="text-green-600" />
          ) : (
            <Upload size={13} style={{ color: '#0F5C5C' }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {uploadState.uploading ? (
            <p className="text-xs" style={mutedStyle}>جاري الرفع...</p>
          ) : uploadState.file ? (
            <p className="text-xs font-semibold truncate" style={uploadState.url ? { color: '#16a34a' } : labelStyle}>
              {uploadState.file.name}
            </p>
          ) : (
            <p className="text-xs" style={mutedStyle}>اختر ملفاً (PDF أو صورة)</p>
          )}
        </div>
        {!uploadState.url && !uploadState.uploading && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg shrink-0" style={{ background: 'rgba(15,92,92,0.1)', color: '#0F5C5C' }}>
            رفع
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept || 'image/*,application/pdf'}
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onChange(f); }}
        />
      </div>
      {uploadState.error && (
        <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle size={10} /> {uploadState.error}
        </p>
      )}
    </div>
  );
}

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [signupStep, setSignupStep] = useState<SignupStep>(0);
  const [email, setEmail] = useState('');
  const [loginCrNumber, setLoginCrNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [lang, setLang] = useState<Lang>('ar');
  const [theme, setTheme] = useState<Theme>('light');
  const [signupData, setSignupData] = useState<SignupData>({
    companyNameAr: '', companyNameEn: '', crNumber: '', vatNumber: '',
    city: '', phone: '', buildingNumber: '', streetName: '', district: '', postalCode: '', additionalNumber: '',
    packageId: '', adminName: '', adminEmail: '', adminPassword: '', confirmPassword: ''
  });

  // Document upload states
  const [crDoc, setCrDoc] = useState<UploadState>({ file: null, url: '', uploading: false, error: '' });
  const [vatDoc, setVatDoc] = useState<UploadState>({ file: null, url: '', uploading: false, error: '' });
  const [paymentDoc, setPaymentDoc] = useState<UploadState>({ file: null, url: '', uploading: false, error: '' });

  const [superAdminOpen, setSuperAdminOpen] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [superAdminEmailInput, setSuperAdminEmailInput] = useState('');
  const [superAdminPasswordInput, setSuperAdminPasswordInput] = useState('');
  const [superAdminAuthStep, setSuperAdminAuthStep] = useState<'idle' | 'verify'>('idle');
  const [superAdminError, setSuperAdminError] = useState('');
  const [superAdminLoading, setSuperAdminLoading] = useState(false);
  const logoClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Contact Us state
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', subject: '', description: '' });
  const [contactAttachment, setContactAttachment] = useState<UploadState>({ file: null, url: '', uploading: false, error: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');
  const contactAttachRef = useRef<HTMLInputElement>(null);

  const { signIn, signInDemo, resetPassword } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const mountedRef = React.useRef(true);

  const tx = t[lang];
  const isRtl = lang === 'ar';
  const isDark = theme === 'dark';

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    if (logoClickTimerRef.current) clearTimeout(logoClickTimerRef.current);
    if (newCount >= 3) {
      setLogoClickCount(0);
      setSuperAdminAuthStep('verify');
      setSuperAdminEmailInput('');
      setSuperAdminError('');
    } else {
      logoClickTimerRef.current = setTimeout(() => setLogoClickCount(0), 1500);
    }
  };

  const handleSuperAdminVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuperAdminError('');
    setSuperAdminLoading(true);
    try {
      const enteredEmail = superAdminEmailInput.trim().toLowerCase();
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: enteredEmail,
        password: superAdminPasswordInput,
      });
      if (authError) throw authError;
      if (data.user) {
        try { await supabase.rpc('get_or_promote_super_admin'); } catch { /* non-blocking */ }
        try {
          await supabase.from('admin_audit_logs').insert([{
            admin_id: data.user.id,
            admin_email: data.user.email,
            action: 'super_admin_login',
            details: { method: 'triple_click_modal', timestamp: new Date().toISOString() },
          }]);
        } catch { /* non-blocking */ }
        setSuperAdminAuthStep('idle');
        setSuperAdminOpen(true);
        setSuperAdminError('');
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Invalid') || msg.includes('credentials') || msg.includes('invalid_credentials')) {
        setSuperAdminError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
      } else {
        setSuperAdminError(msg || 'حدث خطأ أثناء تسجيل الدخول');
      }
    } finally {
      setSuperAdminLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (logoClickTimerRef.current) clearTimeout(logoClickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    supabase.from('subscription_packages').select('*').eq('is_active', true).order('sort_order').then(({ data }) => {
      if (data && mountedRef.current) setPackages(data as Package[]);
    });
  }, []);

  // Upload a file to Supabase Storage
  const uploadDocument = async (
    file: File,
    docType: 'cr' | 'vat' | 'payment',
    setState: React.Dispatch<React.SetStateAction<UploadState>>
  ) => {
    setState(s => ({ ...s, file, uploading: true, error: '' }));
    try {
      const ext = file.name.split('.').pop() || 'pdf';
      const path = `join-requests/${docType}-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error: uploadErr } = await supabase.storage
        .from('company-documents')
        .upload(path, file, { upsert: false });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('company-documents').getPublicUrl(data.path);
      setState(s => ({ ...s, url: urlData?.publicUrl || data.path, uploading: false }));
    } catch (err: any) {
      setState(s => ({ ...s, uploading: false, error: err?.message || 'خطأ في رفع الملف' }));
    }
  };

  // Theme CSS variables
  const darkVars = isDark ? {
    '--auth-bg': '#0D1117',
    '--auth-panel-bg': '#161B22',
    '--auth-card-bg': '#1C2128',
    '--auth-border': 'rgba(255,255,255,0.1)',
    '--auth-text': '#E6EDF3',
    '--auth-text-muted': '#8B949E',
    '--auth-input-bg': '#21262D',
    '--auth-input-border': 'rgba(255,255,255,0.12)',
  } as React.CSSProperties : {
    '--auth-bg': '#F0F4F8',
    '--auth-panel-bg': '#F7F8FA',
    '--auth-card-bg': '#FFFFFF',
    '--auth-border': '#E1E5EC',
    '--auth-text': '#0D1117',
    '--auth-text-muted': '#64748B',
    '--auth-input-bg': '#F0F2F7',
    '--auth-input-border': '#E1E5EC',
  } as React.CSSProperties;

  const inputClass = `w-full h-11 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/25 focus:border-[#0F5C5C] transition-all px-3 border font-medium`;
  const inputStyle: React.CSSProperties = {
    background: 'var(--auth-input-bg)',
    borderColor: 'var(--auth-input-border)',
    color: 'var(--auth-text)',
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const identifier = loginCrNumber.trim();

      // Demo accounts: allow email login for demo users
      const isDemoAttempt = demoCredentials.some(c => c.email.toLowerCase() === identifier.toLowerCase());
      if (isDemoAttempt) {
        const loggedInAsDemo = signInDemo(identifier.toLowerCase(), password);
        if (loggedInAsDemo) { router.push('/'); return; }
      }

      // Resolve email from cr_number via RPC
      let resolvedEmail = identifier;
      if (!identifier.includes('@')) {
        const { data: emailFromCr, error: rpcError } = await supabase.rpc('get_email_by_cr_number', {
          p_cr_number: identifier,
        });
        if (rpcError || !emailFromCr) {
          setError(lang === 'ar' ? 'لم يتم العثور على شركة مسجّلة بهذا السجل التجاري. تأكد من الرقم وأعد المحاولة.' : 'No company found with this CR number. Please verify and try again.');
          return;
        }
        resolvedEmail = emailFromCr as string;
      }

      // Sign in with resolved email
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: resolvedEmail.toLowerCase(),
        password,
      });

      if (signInError) {
        const msg = signInError.message || '';
        if (msg.includes('Invalid') || msg.includes('credentials') || msg.includes('invalid_credentials')) {
          setError(lang === 'ar' ? 'البريد الإلكتروني أو السجل التجاري أو كلمة المرور غير صحيحة — يمكنك استخدام الحسابات التجريبية أدناه' : 'Invalid email, CR number or password — use demo accounts below');
        } else {
          setError(msg || (lang === 'ar' ? 'حدث خطأ أثناء تسجيل الدخول' : 'Login error occurred'));
        }
        return;
      }

      if (!signInData?.user) {
        setError(lang === 'ar' ? 'حدث خطأ أثناء تسجيل الدخول' : 'Login error occurred');
        return;
      }

      // Check if first login (password_set = false)
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('password_set')
        .eq('id', signInData.user.id)
        .single();

      if (profileData && profileData.password_set === false) {
        setNeedsPasswordSetup(true);
        return;
      }

      router.push('/');
    } catch (err: any) {
      if (!mountedRef.current) return;
      const loggedInAsDemo = signInDemo(loginCrNumber.trim().toLowerCase(), password);
      if (loggedInAsDemo) { router.push('/'); return; }
      const msg = err?.message || '';
      if (msg.includes('Invalid') || msg.includes('credentials') || msg.includes('invalid_credentials')) {
        setError(lang === 'ar' ? 'البريد الإلكتروني أو السجل التجاري أو كلمة المرور غير صحيحة — يمكنك استخدام الحسابات التجريبية أدناه' : 'Invalid email, CR number or password — use demo accounts below');
      } else {
        setError(msg || (lang === 'ar' ? 'حدث خطأ أثناء تسجيل الدخول' : 'Login error occurred'));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(email);
      if (mountedRef.current) setSuccess(lang === 'ar' ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني' : 'Password reset link sent to your email');
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || (lang === 'ar' ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const validateStep0 = () => {
    if (!signupData.companyNameAr) return lang === 'ar' ? 'اسم الشركة بالعربي مطلوب' : 'Arabic company name is required';
    if (!/^\d{10}$/.test(signupData.crNumber)) return lang === 'ar' ? 'السجل التجاري يجب أن يكون 10 أرقام' : 'CR must be 10 digits';
    if (!/^3\d{14}$/.test(signupData.vatNumber)) return lang === 'ar' ? 'الرقم الضريبي يجب أن يكون 15 رقماً يبدأ بـ 3' : 'VAT must be 15 digits starting with 3';
    if (!signupData.city) return lang === 'ar' ? 'المدينة مطلوبة' : 'City is required';
    if (!/^05\d{8}$/.test(signupData.phone)) return lang === 'ar' ? 'رقم الجوال غير صالح (05XXXXXXXX)' : 'Invalid mobile number (05XXXXXXXX)';
    if (!/^\d{4}$/.test(signupData.buildingNumber)) return lang === 'ar' ? 'رقم المبنى يجب أن يكون 4 أرقام' : 'Building number must be 4 digits';
    if (!signupData.streetName) return lang === 'ar' ? 'اسم الشارع مطلوب' : 'Street name is required';
    if (!signupData.district) return lang === 'ar' ? 'الحي مطلوب' : 'District is required';
    if (!/^\d{5}$/.test(signupData.postalCode)) return lang === 'ar' ? 'الرمز البريدي يجب أن يكون 5 أرقام' : 'Postal code must be 5 digits';
    if (!signupData.packageId) return lang === 'ar' ? 'يرجى اختيار الباقة المناسبة' : 'Please select a package';
    return '';
  };

  const validateStep1 = () => {
    if (!signupData.adminName) return lang === 'ar' ? 'الاسم مطلوب' : 'Name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.adminEmail)) return lang === 'ar' ? 'بريد إلكتروني غير صالح' : 'Invalid email';
    if (signupData.adminPassword.length < 8) return lang === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters';
    if (signupData.adminPassword !== signupData.confirmPassword) return lang === 'ar' ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match';
    return '';
  };

  const handleSignupNext = () => {
    const err = validateStep0();
    if (err) { setError(err); return; }
    setError('');
    setSignupStep(1);
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateStep1();
    if (err) { setError(err); return; }
    // Check if any doc is still uploading
    if (crDoc.uploading || vatDoc.uploading || paymentDoc.uploading) {
      setError(lang === 'ar' ? 'يرجى الانتظار حتى اكتمال رفع الملفات' : 'Please wait for file uploads to complete');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const selectedPkg = packages.find((p) => p.id === signupData.packageId);
      await supabase.from('join_requests').insert([{
        company_name_ar: signupData.companyNameAr, company_name_en: signupData.companyNameEn,
        cr_number: signupData.crNumber, vat_number: signupData.vatNumber,
        city: signupData.city, phone: signupData.phone,
        building_number: signupData.buildingNumber || null,
        street_name: signupData.streetName || null,
        district: signupData.district || null,
        postal_code: signupData.postalCode || null,
        additional_number: signupData.additionalNumber || null,
        admin_name: signupData.adminName, admin_email: signupData.adminEmail,
        admin_password: signupData.adminPassword,
        requested_package_id: signupData.packageId || null,
        requested_package_name: selectedPkg ? (lang === 'en' ? (selectedPkg.name_en || selectedPkg.name_ar) : selectedPkg.name_ar) : null,
        cr_document_url: crDoc.url || null,
        vat_document_url: vatDoc.url || null,
        payment_proof_url: paymentDoc.url || null,
        status: 'pending'
      }]);
      if (!mountedRef.current) return;
      setSignupStep(2);
      setSuccess(`${lang === 'ar' ? 'تم تقديم طلب الانضمام بنجاح! الباقة المختارة:' : 'Join request submitted! Package:'} ${selectedPkg?.name_ar || ''}`);
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || (lang === 'ar' ? 'حدث خطأ أثناء تقديم الطلب' : 'Error submitting request'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const fillDemo = async (cred: typeof demoCredentials[0]) => {
    setLoginCrNumber(cred.email);
    setPassword(cred.password);
    setError('');
    setLoading(true);
    try {
      const loggedInAsDemo = signInDemo(cred.email, cred.password);
      if (loggedInAsDemo) { router.push('/'); return; }
      await signIn(cred.email, cred.password);
      router.push('/');
    } catch {
      if (!mountedRef.current) return;
      const retryDemo = signInDemo(cred.email, cred.password);
      if (retryDemo) { router.push('/'); return; }
      setError(lang === 'ar' ? 'حدث خطأ أثناء تسجيل الدخول التجريبي' : 'Demo login error occurred');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const selectedPkg = packages.find((p) => p.id === signupData.packageId);

  const cardStyle: React.CSSProperties = {
    background: 'var(--auth-card-bg)',
    border: '1px solid var(--auth-border)',
    color: 'var(--auth-text)',
  };

  const labelStyle: React.CSSProperties = { color: 'var(--auth-text)' };
  const mutedStyle: React.CSSProperties = { color: 'var(--auth-text-muted)' };

  const handleContactAttach = async (file: File) => {
    setContactAttachment(s => ({ ...s, file, uploading: true, error: '' }));
    try {
      const ext = file.name.split('.').pop() || 'pdf';
      const path = `contact-attachments/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error: uploadErr } = await supabase.storage
        .from('company-documents')
        .upload(path, file, { upsert: false });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('company-documents').getPublicUrl(data.path);
      setContactAttachment(s => ({ ...s, url: urlData?.publicUrl || data.path, uploading: false }));
    } catch (err: any) {
      setContactAttachment(s => ({ ...s, uploading: false, error: err?.message || 'خطأ في رفع الملف' }));
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone || !contactForm.email || !contactForm.subject || !contactForm.description) {
      setContactError(lang === 'ar' ? 'يرجى تعبئة جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    if (contactAttachment.uploading) {
      setContactError(lang === 'ar' ? 'يرجى الانتظار حتى اكتمال رفع الملف' : 'Please wait for file upload to complete');
      return;
    }
    setContactError('');
    setContactSending(true);
    try {
      const { error: insertErr } = await supabase.from('contact_us_messages').insert([{
        name: contactForm.name,
        phone: contactForm.phone,
        email: contactForm.email,
        subject: contactForm.subject,
        description: contactForm.description,
        attachment_url: contactAttachment.url || null,
        attachment_name: contactAttachment.file?.name || null,
        status: 'new',
      }]);
      if (insertErr) throw insertErr;
      setContactSent(true);
      setContactForm({ name: '', phone: '', email: '', subject: '', description: '' });
      setContactAttachment({ file: null, url: '', uploading: false, error: '' });
    } catch (err: any) {
      setContactError(err?.message || (lang === 'ar' ? tx.contactError : tx.contactError));
    } finally {
      setContactSending(false);
    }
  };

  const handleCloseContact = () => {
    setContactOpen(false);
    setContactSent(false);
    setContactError('');
    setContactForm({ name: '', phone: '', email: '', subject: '', description: '' });
    setContactAttachment({ file: null, url: '', uploading: false, error: '' });
  };

  return (
    <div
      className="min-h-screen flex"
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{ ...darkVars, background: 'var(--auth-bg)', fontFamily: isRtl ? "'Noto Kufi Arabic', sans-serif" : "'IBM Plex Sans Arabic', sans-serif" }}
    >
      {/* Super Admin Email Verification Modal */}
      {superAdminAuthStep === 'verify' && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md">
          <div className="rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 relative border" style={cardStyle} dir="rtl">
            <button onClick={() => { setSuperAdminAuthStep('idle'); setSuperAdminError(''); }}
              className="absolute top-4 left-4 p-1.5 rounded-lg transition-colors"
              style={{ background: 'var(--auth-input-bg)', color: 'var(--auth-text-muted)' }}>
              <X size={16} />
            </button>
            <div className="flex flex-col items-center mb-6">
              <div className="relative mb-3">
                <div className="absolute inset-0 rounded-2xl blur-xl opacity-60" style={{ background: 'radial-gradient(circle, rgba(15,92,92,0.8) 0%, transparent 70%)' }} />
                <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center border-2"
                  style={{ background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', borderColor: 'rgba(201,168,76,0.5)', boxShadow: '0 8px 32px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                  <NexoraMark size={40} variant="white" />
                </div>
              </div>
              <h2 className="text-lg font-bold" style={labelStyle}>{tx.superAdminPanel}</h2>
              <p className="text-xs mt-1 text-center" style={mutedStyle}>{tx.superAdminPanelSub}</p>
            </div>
            <form onSubmit={handleSuperAdminVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>{tx.superAdminEmailLabel}</label>
                <div className="relative">
                  <Mail size={15} className="absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none" style={mutedStyle} />
                  <input type="email" value={superAdminEmailInput}
                    onChange={e => { setSuperAdminEmailInput(e.target.value); setSuperAdminError(''); }}
                    autoFocus placeholder="superadmin@nexora.sa"
                    className={`${inputClass} pe-9`} style={inputStyle} dir="ltr" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>كلمة المرور</label>
                <div className="relative">
                  <Lock size={15} className="absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none" style={mutedStyle} />
                  <input type="password" value={superAdminPasswordInput}
                    onChange={e => { setSuperAdminPasswordInput(e.target.value); setSuperAdminError(''); }}
                    placeholder="••••••••"
                    className={`${inputClass} pe-9`} style={inputStyle} dir="ltr" />
                </div>
                {superAdminError && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <AlertCircle size={12} /> {superAdminError}
                  </p>
                )}
              </div>
              <button type="submit" disabled={superAdminLoading}
                className="w-full h-11 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', boxShadow: '0 4px 16px rgba(15,92,92,0.4)' }}>
                {superAdminLoading ? <Loader2 size={15} className="animate-spin" /> : <><Shield size={15} /><span>{tx.superAdminEnter}</span></>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Super Admin Full Panel */}
      {superAdminOpen && (
        <div className="fixed inset-0 z-[9998] overflow-auto" style={{ background: isDark ? '#0D1117' : '#F7F8FA' }}>
          <div className="relative">
            <button onClick={() => setSuperAdminOpen(false)}
              className="fixed top-4 left-4 z-[9999] flex items-center gap-1.5 px-3 py-2 rounded-xl shadow-md text-sm font-semibold transition-all hover:text-red-600 border"
              style={{ background: 'var(--auth-card-bg)', borderColor: 'var(--auth-border)', color: 'var(--auth-text-muted)' }}>
              <X size={14} />
              <span>إغلاق</span>
            </button>
            <SuperAdminDashboard />
          </div>
        </div>
      )}

      {/* ── Contact Us Modal ── */}
      {contactOpen && (
        <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="rounded-2xl shadow-2xl w-full max-w-md mx-auto relative border overflow-hidden" style={cardStyle}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--auth-border)', background: isDark ? 'rgba(15,92,92,0.15)' : 'rgba(15,92,92,0.06)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)' }}>
                  <MessageSquare size={16} className="text-white" />
                </div>
                <div>
                  <h2 className="text-base font-black" style={labelStyle}>{tx.contactUsTitle}</h2>
                  <p className="text-[11px]" style={mutedStyle}>{tx.contactUsSub}</p>
                </div>
              </div>
              <button onClick={handleCloseContact} className="p-1.5 rounded-lg transition-colors hover:bg-red-50 hover:text-red-500" style={{ color: 'var(--auth-text-muted)' }}>
                <X size={16} />
              </button>
            </div>

            <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
              {contactSent ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-black mb-2" style={labelStyle}>{lang === 'ar' ? 'تم الإرسال بنجاح!' : 'Sent Successfully!'}</h3>
                  <p className="text-sm leading-relaxed mb-6" style={mutedStyle}>{tx.contactSuccess}</p>
                  <button onClick={handleCloseContact}
                    className="h-10 px-6 text-white rounded-xl text-sm font-bold transition-all hover:brightness-110"
                    style={{ background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)' }}>
                    {lang === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {contactError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm" style={isDark ? { background: 'rgba(220,38,38,0.1)', borderColor: 'rgba(220,38,38,0.3)', color: '#FCA5A5' } : {}}>
                      <AlertCircle size={14} className="shrink-0" />
                      <span>{contactError}</span>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactName} <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                        <User size={13} style={{ color: '#0F5C5C' }} />
                      </div>
                      <input type="text" value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                        className={`${inputClass} pe-11`} style={inputStyle} placeholder={tx.contactNamePlaceholder} />
                    </div>
                  </div>

                  {/* Phone + Email */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactPhone} <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                          <Phone size={13} style={{ color: '#0F5C5C' }} />
                        </div>
                        <input type="tel" value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                          className={`${inputClass} pe-11`} style={inputStyle} placeholder={tx.contactPhonePlaceholder} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactEmail} <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                          <Mail size={13} style={{ color: '#0F5C5C' }} />
                        </div>
                        <input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                          className={`${inputClass} pe-11`} style={inputStyle} placeholder={tx.contactEmailPlaceholder} />
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactSubject} <span className="text-red-500">*</span></label>
                    <input type="text" value={contactForm.subject} onChange={e => setContactForm(f => ({ ...f, subject: e.target.value }))}
                      className={inputClass} style={inputStyle} placeholder={tx.contactSubjectPlaceholder} />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactDescription} <span className="text-red-500">*</span></label>
                    <textarea value={contactForm.description} onChange={e => setContactForm(f => ({ ...f, description: e.target.value }))}
                      rows={4} placeholder={tx.contactDescriptionPlaceholder}
                      className="w-full rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/25 focus:border-[#0F5C5C] transition-all px-3 py-2.5 border font-medium resize-none"
                      style={inputStyle} />
                  </div>

                  {/* Attachment */}
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.contactAttachment}</label>
                    <div
                      onClick={() => !contactAttachment.uploading && contactAttachRef.current?.click()}
                      className={`relative flex items-center gap-2.5 h-11 rounded-xl border cursor-pointer transition-all px-3 ${contactAttachment.uploading ? 'opacity-70 cursor-not-allowed' : 'hover:border-[#0F5C5C]'}`}
                      style={{
                        ...inputStyle,
                        borderColor: contactAttachment.url ? '#16a34a' : contactAttachment.file ? '#0F5C5C' : 'var(--auth-input-border)',
                        background: contactAttachment.url ? (isDark ? 'rgba(22,163,74,0.08)' : '#f0fdf4') : 'var(--auth-input-bg)',
                      }}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${contactAttachment.url ? 'bg-green-100' : 'bg-[rgba(15,92,92,0.1)]'}`}>
                        {contactAttachment.uploading ? (
                          <Loader2 size={13} className="animate-spin text-[#0F5C5C]" />
                        ) : contactAttachment.url ? (
                          <CheckCircle size={13} className="text-green-600" />
                        ) : (
                          <Paperclip size={13} style={{ color: '#0F5C5C' }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {contactAttachment.uploading ? (
                          <p className="text-xs" style={mutedStyle}>{tx.contactUploading}</p>
                        ) : contactAttachment.file ? (
                          <p className="text-xs font-semibold truncate" style={contactAttachment.url ? { color: '#16a34a' } : labelStyle}>
                            {contactAttachment.file.name}
                          </p>
                        ) : (
                          <p className="text-xs" style={mutedStyle}>{tx.contactAttachmentSub}</p>
                        )}
                      </div>
                      {!contactAttachment.url && !contactAttachment.uploading && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg shrink-0" style={{ background: 'rgba(15,92,92,0.1)', color: '#0F5C5C' }}>
                          {tx.contactAttachFile}
                        </span>
                      )}
                      <input ref={contactAttachRef} type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleContactAttach(f); }} />
                    </div>
                    {contactAttachment.error && (
                      <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={10} /> {contactAttachment.error}
                      </p>
                    )}
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={contactSending}
                    className="w-full h-11 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                    {contactSending ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /><span>{tx.contactSend}</span></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Brand Panel (Desktop) ── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[440px] shrink-0 p-10 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0F5C5C 0%, #0A3D3D 50%, #072828 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />
          <div className="absolute top-1/3 -left-20 w-56 h-56 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)' }} />
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(15,92,92,0.4) 0%, transparent 70%)' }} />
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="absolute top-20 left-10 w-3 h-3 rounded-full bg-[#C9A84C] opacity-60" style={{ boxShadow: '0 0 20px 8px rgba(201,168,76,0.3)' }} />
          <div className="absolute bottom-32 right-16 w-2 h-2 rounded-full bg-white opacity-40" style={{ boxShadow: '0 0 15px 6px rgba(255,255,255,0.2)' }} />
        </div>

        <div className="relative z-10">
          <div className="mb-10">
            <button type="button" onClick={handleLogoClick} className="focus:outline-none select-none cursor-pointer block mb-6">
              <div className="relative inline-flex">
                <div className="absolute inset-0 rounded-2xl blur-2xl opacity-70" style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.5) 0%, rgba(15,92,92,0.3) 100%)' }} />
                <div className="relative flex items-center gap-4 px-5 py-3 rounded-2xl border"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
                    borderColor: 'rgba(201,168,76,0.4)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(201,168,76,0.1)',
                  }}>
                  <div className="relative">
                    <div className="absolute inset-0 rounded-xl blur-lg opacity-80" style={{ background: 'rgba(201,168,76,0.4)' }} />
                    <div className="relative w-12 h-12 rounded-xl flex items-center justify-center border"
                      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))', borderColor: 'rgba(255,255,255,0.2)' }}>
                      <NexoraMark size={36} variant="white" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-black tracking-tight text-3xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>Nexora</span>
                    </div>
                    <span className="text-[#C9A84C] tracking-[0.2em] uppercase text-[10px] font-black">{'      ' + tx.platformName.split(' ').slice(3).join(' ') + ' '}</span>
                  </div>
                </div>
              </div>
            </button>

            <h2 className="text-white mb-3 text-[27px] font-black" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              {tx.platformName.split(' ').slice(0, 3).join(' ')}<br />
              <span className="text-[#C9A84C] font-bold">{tx.platformName.split(' ').slice(3).join(' ')}</span>
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">{tx.platformSub}</p>
          </div>

          <div className="space-y-3">
            {[
              { icon: Zap, text: tx.features[0] },
              { icon: Building2, text: tx.features[1] },
              { icon: Shield, text: tx.features[2] },
              { icon: BarChart2, text: tx.features[3] },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 group">
                <div className="relative shrink-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center border transition-all group-hover:scale-105"
                    style={{
                      background: 'linear-gradient(135deg, rgba(201,168,76,0.2), rgba(201,168,76,0.05))',
                      borderColor: 'rgba(201,168,76,0.3)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                    }}>
                    <f.icon size={15} className="text-[#C9A84C]" />
                  </div>
                </div>
                <span className="text-white/75 text-sm font-medium group-hover:text-white/95 transition-colors">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="h-px mb-4" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' }} />
          <p className="text-white/25 text-xs">{tx.copyright}</p>
        </div>
      </div>

      {/* ── Form Panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto relative" style={{ background: 'var(--auth-panel-bg)' }}>
        {/* Top controls bar */}
        <div className="absolute top-4 end-4 flex items-center gap-2">
          <button
            onClick={() => setLang(l => l === 'ar' ? 'en' : 'ar')}
            className="flex items-center gap-1.5 h-9 px-3 rounded-xl text-xs font-bold transition-all border hover:scale-105"
            style={{ background: 'var(--auth-card-bg)', borderColor: 'var(--auth-border)', color: 'var(--auth-text)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          >
            <Globe size={13} />
            <span>{lang === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
          <button
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            className="flex items-center justify-center w-9 h-9 rounded-xl transition-all border hover:scale-105"
            style={{ background: isDark ? 'rgba(201,168,76,0.15)' : 'var(--auth-card-bg)', borderColor: isDark ? 'rgba(201,168,76,0.3)' : 'var(--auth-border)', color: isDark ? '#C9A84C' : 'var(--auth-text)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          >
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>

        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <button type="button" onClick={handleLogoClick} className="focus:outline-none select-none cursor-pointer">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl blur-lg opacity-50" style={{ background: 'rgba(15,92,92,0.4)' }} />
              <div className="relative w-10 h-10 rounded-xl flex items-center justify-center border"
                style={{ background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', borderColor: 'rgba(201,168,76,0.4)', boxShadow: '0 4px 16px rgba(15,92,92,0.3)' }}>
                <NexoraMark size={26} variant="white" />
              </div>
            </div>
          </button>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[#0F5C5C] font-black text-xl tracking-tight">Nexora</span>
            </div>
            <span className="text-[10px] tracking-widest uppercase font-bold" style={mutedStyle}>ERP</span>
          </div>
        </div>

        <div className="w-full max-w-[460px]">
          {/* Mode tabs */}
          {mode !== 'forgot' && (
            <div className="flex rounded-xl p-1 mb-6 border" style={{ background: 'var(--auth-card-bg)', borderColor: 'var(--auth-border)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'login' ? 'text-white shadow-md' : ''}`}
                style={mode === 'login' ? { background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', boxShadow: '0 4px 12px rgba(15,92,92,0.3)' } : { color: 'var(--auth-text-muted)' }}
              >
                {tx.login}
              </button>
              <button
                onClick={() => { setMode('signup'); setError(''); setSuccess(''); setSignupStep(0); }}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'signup' ? 'text-white shadow-md' : ''}`}
                style={mode === 'signup' ? { background: 'linear-gradient(135deg, #0F5C5C, #0A3D3D)', boxShadow: '0 4px 12px rgba(15,92,92,0.3)' } : { color: 'var(--auth-text-muted)' }}
              >
                {tx.signup}
              </button>
            </div>
          )}

          {/* Alerts */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 mb-4 text-sm" style={isDark ? { background: 'rgba(220,38,38,0.1)', borderColor: 'rgba(220,38,38,0.3)', color: '#FCA5A5' } : {}}>
              <AlertCircle size={15} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-3 py-2.5 mb-4 text-sm" style={isDark ? { background: 'rgba(22,163,74,0.1)', borderColor: 'rgba(22,163,74,0.3)', color: '#86EFAC' } : {}}>
              <CheckCircle size={15} className="shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* ── LOGIN ── */}
          {mode === 'login' && !needsPasswordSetup && (
            <div className="rounded-2xl border shadow-lg p-6" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div className="mb-5">
                <h1 className="text-xl font-black" style={labelStyle}>{tx.welcome}</h1>
                <p className="text-sm mt-0.5" style={mutedStyle}>{lang === 'ar' ? '' : 'Enter your email or CR number and password to sign in'}</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{lang === 'ar' ? 'البريد الإلكتروني أو رقم السجل التجاري' : 'Email or Commercial Registration No.'}</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
                      {loginCrNumber.includes('@') ? <Mail size={13} style={{ color: '#0F5C5C' }} /> : <Hash size={13} style={{ color: '#0F5C5C' }} />}
                    </div>
                    <input type="text" value={loginCrNumber} onChange={(e) => setLoginCrNumber(e.target.value)} required placeholder={lang === 'ar' ? 'أدخل البريد الإلكتروني أو رقم السجل التجاري' : 'Enter email or CR number'} className={`${inputClass} pe-11`} style={inputStyle} dir="auto" autoComplete="username" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-bold" style={labelStyle}>{tx.password}</label>
                    <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} className="text-xs font-semibold text-[#0F5C5C] hover:underline">{tx.forgot}</button>
                  </div>
                  <div className="relative">
                    <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                      <Lock size={13} style={{ color: '#0F5C5C' }} />
                    </div>
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className={`${inputClass} pe-11 ps-10`} style={inputStyle} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute top-1/2 -translate-y-1/2 start-3 transition-colors" style={mutedStyle}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full h-11 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <><span>{tx.loginBtn}</span><ArrowRight size={16} /></>}
                </button>
              </form>

              {/* Demo credentials */}
              <div className="mt-5 rounded-xl overflow-hidden border" style={{ borderColor: 'var(--auth-border)' }}>
                <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#F7F8FA' }}>
                  <p className="text-xs font-bold" style={mutedStyle}>{tx.demoTitle}</p>
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{tx.demo}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--auth-border)' }}>
                  {demoCredentials.map((cred, i) => (
                    <div key={i} onClick={() => fillDemo(cred)}
                      className="flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors hover:bg-[#0F5C5C]/5"
                      style={{ borderTop: i > 0 ? '1px solid var(--auth-border)' : 'none' }}>
                      <div>
                        <p className="text-xs font-bold" style={labelStyle}>{lang === 'ar' ? cred.role : cred.roleEn}</p>
                        <p className="text-[10px] font-mono" style={mutedStyle}>{cred.email}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cred.color}`}>{tx.use}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── FIRST LOGIN: Set Password ── */}
          {mode === 'login' && needsPasswordSetup && (
            <div className="rounded-2xl border shadow-lg p-6" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <SetPasswordForm onPasswordSet={() => { setNeedsPasswordSetup(false); router.push('/'); }} />
            </div>
          )}

          {/* ── SIGNUP Step 0 ── */}
          {mode === 'signup' && signupStep === 0 && (
            <div className="rounded-2xl border shadow-lg p-6" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <StepIndicator steps={[tx.companyInfo, tx.adminAccount]} currentStep={signupStep} mutedStyle={mutedStyle} />

              <div className="space-y-4 max-h-[65vh] overflow-y-auto pe-1">
                <div>
                  <h2 className="text-lg font-black" style={labelStyle}>{tx.companyInfoTitle}</h2>
                  <p className="text-xs mt-0.5" style={mutedStyle}>{tx.companyInfoSub}</p>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.companyNameAr} <span className="text-red-500">*</span></label>
                  <input type="text" value={signupData.companyNameAr} onChange={(e) => setSignupData((d) => ({ ...d, companyNameAr: e.target.value }))} className={inputClass} style={inputStyle} placeholder="شركة النخبة للتجزئة" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.companyNameEn}</label>
                  <input type="text" value={signupData.companyNameEn} onChange={(e) => setSignupData((d) => ({ ...d, companyNameEn: e.target.value }))} className={inputClass} style={inputStyle} placeholder="Al Nakhlah Retail Co." dir="ltr" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.crNumber} <span className="text-red-500">*</span></label>
                    <input type="text" value={signupData.crNumber} onChange={(e) => setSignupData((d) => ({ ...d, crNumber: e.target.value }))} className={inputClass} style={inputStyle} placeholder="1010234567" maxLength={10} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.vatNumber} <span className="text-red-500">*</span></label>
                    <input type="text" value={signupData.vatNumber} onChange={(e) => setSignupData((d) => ({ ...d, vatNumber: e.target.value }))} className={inputClass} style={inputStyle} placeholder="310234567800003" maxLength={15} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.city} <span className="text-red-500">*</span></label>
                    <select value={signupData.city} onChange={(e) => setSignupData((d) => ({ ...d, city: e.target.value }))} className={inputClass} style={inputStyle}>
                      <option value="">{tx.selectCity}</option>
                      {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.phone} <span className="text-red-500">*</span></label>
                    <input type="tel" value={signupData.phone} onChange={(e) => setSignupData((d) => ({ ...d, phone: e.target.value }))} className={inputClass} style={inputStyle} placeholder="05XXXXXXXX" />
                  </div>
                </div>

                {/* National Address */}
                <div className="rounded-xl p-4 border" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F7F8FA', borderColor: 'var(--auth-border)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.15)' }}>
                      <Navigation size={12} style={{ color: '#0F5C5C' }} />
                    </div>
                    <h3 className="text-sm font-black" style={labelStyle}>{tx.nationalAddress}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(15,92,92,0.1)', color: '#0F5C5C' }}>{tx.zatcaRequired}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: tx.buildingNumber, key: 'buildingNumber', placeholder: '1234', maxLength: 4 },
                      { label: tx.streetName, key: 'streetName', placeholder: 'شارع الملك فهد' },
                      { label: tx.district, key: 'district', placeholder: 'حي العليا' },
                      { label: tx.postalCode, key: 'postalCode', placeholder: '12345', maxLength: 5 },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-bold mb-1" style={labelStyle}>{field.label} <span className="text-red-500">*</span></label>
                        <input type="text" value={(signupData as any)[field.key]}
                          onChange={(e) => setSignupData((d) => ({ ...d, [field.key]: e.target.value }))}
                          className="w-full h-10 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C] border transition-all"
                          style={{ ...inputStyle, background: 'var(--auth-card-bg)' }}
                          placeholder={field.placeholder} maxLength={field.maxLength} />
                      </div>
                    ))}
                    <div className="col-span-2">
                      <label className="block text-xs font-bold mb-1" style={labelStyle}>{tx.additionalNumber} <span className="text-xs font-normal" style={mutedStyle}>{tx.optional}</span></label>
                      <input type="text" value={signupData.additionalNumber} onChange={(e) => setSignupData((d) => ({ ...d, additionalNumber: e.target.value }))}
                        className="w-full h-10 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C] border transition-all"
                        style={{ ...inputStyle, background: 'var(--auth-card-bg)' }} placeholder="6789" maxLength={4} />
                    </div>
                  </div>
                </div>

                {/* Package Selection */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.15)' }}>
                      <Package size={12} style={{ color: '#0F5C5C' }} />
                    </div>
                    <h3 className="text-sm font-black" style={labelStyle}>{tx.choosePackage} <span className="text-red-500">*</span></h3>
                  </div>
                  {packages.length === 0 ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={18} className="animate-spin text-[#0F5C5C]" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {packages.map((pkg) => (
                        <div key={pkg.id} onClick={() => setSignupData((d) => ({ ...d, packageId: pkg.id }))}
                          className="rounded-xl p-3 cursor-pointer transition-all border"
                          style={{
                            borderColor: signupData.packageId === pkg.id ? '#0F5C5C' : 'var(--auth-border)',
                            background: signupData.packageId === pkg.id ? 'rgba(15,92,92,0.08)' : 'var(--auth-card-bg)',
                            boxShadow: signupData.packageId === pkg.id ? '0 0 0 1px rgba(15,92,92,0.2)' : 'none',
                          }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${signupData.packageId === pkg.id ? 'border-[#0F5C5C]' : ''}`}
                                style={signupData.packageId !== pkg.id ? { borderColor: 'var(--auth-border)' } : {}}>
                                {signupData.packageId === pkg.id && <div className="w-2 h-2 rounded-full bg-[#0F5C5C]" />}
                              </div>
                              <div>
                                <p className="text-sm font-black" style={labelStyle}>{lang === 'en' ? (pkg.name_en || pkg.name_ar) : pkg.name_ar}</p>
                                <p className="text-[10px]" style={mutedStyle}>{pkg.max_users ? `${tx.upTo} ${pkg.max_users} ${tx.users} · ${pkg.max_branches} ${tx.branches}` : lang === 'ar' ? 'إضافة على باقتك الحالية' : 'Add-on for your current plan'}</p>
                              </div>
                            </div>
                            <div className="text-end">
                              <p className="text-sm font-black text-[#0F5C5C]">{pkg.price_monthly > 0 ? `${pkg.price_monthly} ${lang === 'en' ? 'SAR' : 'ر.س'}${tx.monthlyPrice}` : tx.free}</p>
                              {pkg.price_yearly > 0 && <p className="text-[10px]" style={mutedStyle}>{pkg.price_yearly} {lang === 'en' ? 'SAR' : 'ر.س'}{tx.yearlyPrice}</p>}
                            </div>
                          </div>
                          {(lang === 'en' ? (pkg.description_en || pkg.description_ar) : pkg.description_ar) && (
                            <p className="text-[11px] mt-1.5 pe-6" style={mutedStyle}>{lang === 'en' ? (pkg.description_en || pkg.description_ar) : pkg.description_ar}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Supporting Documents Section ── */}
                <div className="rounded-xl p-4 border" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F7F8FA', borderColor: 'var(--auth-border)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.15)' }}>
                      <Paperclip size={12} style={{ color: '#0F5C5C' }} />
                    </div>
                    <h3 className="text-sm font-black" style={labelStyle}>{tx.supportingDocs}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(201,168,76,0.15)', color: '#92700a' }}>{tx.docsOptional}</span>
                  </div>
                  <p className="text-[11px] mb-3" style={mutedStyle}>{tx.supportingDocsSub}</p>
                  <div className="space-y-3">
                    <FileUploadField
                      label={tx.crDocument}
                      sublabel={tx.crDocumentSub}
                      uploadState={crDoc}
                      onChange={(file) => uploadDocument(file, 'cr', setCrDoc)}
                      inputStyle={inputStyle}
                      labelStyle={labelStyle}
                      mutedStyle={mutedStyle}
                      isDark={isDark}
                    />
                    <FileUploadField
                      label={tx.vatDocument}
                      sublabel={tx.vatDocumentSub}
                      uploadState={vatDoc}
                      onChange={(file) => uploadDocument(file, 'vat', setVatDoc)}
                      inputStyle={inputStyle}
                      labelStyle={labelStyle}
                      mutedStyle={mutedStyle}
                      isDark={isDark}
                    />
                    <FileUploadField
                      label={tx.paymentProof}
                      sublabel={tx.paymentProofSub}
                      uploadState={paymentDoc}
                      onChange={(file) => uploadDocument(file, 'payment', setPaymentDoc)}
                      inputStyle={inputStyle}
                      labelStyle={labelStyle}
                      mutedStyle={mutedStyle}
                      isDark={isDark}
                    />
                  </div>
                </div>
              </div>

              <button type="button" onClick={handleSignupNext}
                className="w-full h-11 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:-translate-y-0.5 mt-4"
                style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                <span>{tx.next}</span><ChevronLeft size={16} />
              </button>
            </div>
          )}

          {/* ── SIGNUP Step 1 ── */}
          {mode === 'signup' && signupStep === 1 && (
            <div className="rounded-2xl border shadow-lg p-6" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <StepIndicator steps={[tx.companyInfo, tx.adminAccount]} currentStep={signupStep} mutedStyle={mutedStyle} />

              {selectedPkg && (
                <div className="mb-4 p-3 rounded-xl flex items-center justify-between border" style={{ background: 'rgba(15,92,92,0.06)', borderColor: 'rgba(15,92,92,0.2)' }}>
                  <div>
                    <p className="text-xs" style={mutedStyle}>{tx.chosenPackage}</p>
                    <p className="text-sm font-black text-[#0F5C5C]">{lang === 'en' ? (selectedPkg.name_en || selectedPkg.name_ar) : selectedPkg.name_ar}</p>
                  </div>
                  <button type="button" onClick={() => setSignupStep(0)} className="text-xs font-semibold text-[#0F5C5C] hover:underline">{tx.change}</button>
                </div>
              )}

              {/* Docs summary if uploaded */}
              {(crDoc.url || vatDoc.url || paymentDoc.url) && (
                <div className="mb-4 p-3 rounded-xl border" style={{ background: 'rgba(22,163,74,0.06)', borderColor: 'rgba(22,163,74,0.2)' }}>
                  <p className="text-xs font-bold text-green-700 mb-1.5 flex items-center gap-1.5">
                    <CheckCircle size={12} /> {lang === 'ar' ? 'المستندات المرفقة' : 'Attached Documents'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {crDoc.url && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><FileText size={9} />{lang === 'ar' ? 'السجل التجاري' : 'CR Certificate'}</span>}
                    {vatDoc.url && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><FileText size={9} />{lang === 'ar' ? 'شهادة الضريبة' : 'VAT Certificate'}</span>}
                    {paymentDoc.url && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><CreditCard size={9} />{lang === 'ar' ? 'سند السداد' : 'Payment Proof'}</span>}
                  </div>
                </div>
              )}

              <form onSubmit={handleSignupSubmit} className="space-y-4">
                <div>
                  <h2 className="text-lg font-black" style={labelStyle}>{tx.adminTitle}</h2>
                  <p className="text-xs mt-0.5" style={mutedStyle}>{tx.adminSub}</p>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.fullName} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                      <User size={13} style={{ color: '#0F5C5C' }} />
                    </div>
                    <input type="text" value={signupData.adminName} onChange={(e) => setSignupData((d) => ({ ...d, adminName: e.target.value }))} className={`${inputClass} pe-11`} style={inputStyle} placeholder="محمد بن عبدالله العتيبي" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.email} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                      <Mail size={13} style={{ color: '#0F5C5C' }} />
                    </div>
                    <input type="email" value={signupData.adminEmail} onChange={(e) => setSignupData((d) => ({ ...d, adminEmail: e.target.value }))} className={`${inputClass} pe-11`} style={inputStyle} placeholder="admin@yourcompany.sa" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.password} <span className="text-red-500">*</span></label>
                  <p className="text-xs mb-1.5" style={mutedStyle}>{tx.passwordMin}</p>
                  <div className="relative">
                    <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                      <Lock size={13} style={{ color: '#0F5C5C' }} />
                    </div>
                    <input type={showPassword ? 'text' : 'password'} value={signupData.adminPassword} onChange={(e) => setSignupData((d) => ({ ...d, adminPassword: e.target.value }))} className={`${inputClass} pe-11 ps-10`} style={inputStyle} placeholder="••••••••••" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute top-1/2 -translate-y-1/2 start-3 transition-colors" style={mutedStyle}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.confirmPassword} <span className="text-red-500">*</span></label>
                  <input type="password" value={signupData.confirmPassword} onChange={(e) => setSignupData((d) => ({ ...d, confirmPassword: e.target.value }))} className={inputClass} style={inputStyle} placeholder="••••••••••" />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setSignupStep(0); setError(''); }}
                    className="flex-1 h-11 rounded-xl text-sm font-bold hover:brightness-95 transition-all flex items-center justify-center gap-2 border"
                    style={{ background: 'var(--auth-input-bg)', color: 'var(--auth-text)', borderColor: 'var(--auth-border)' }}>
                    <ChevronRight size={16} />{tx.back}
                  </button>
                  <button type="submit" disabled={loading}
                    className="flex-1 h-11 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <><span>{tx.submit}</span><ArrowRight size={16} /></>}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── SIGNUP SUCCESS ── */}
          {mode === 'signup' && signupStep === 2 && (
            <div className="rounded-2xl border shadow-lg p-8 text-center" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" style={isDark ? { background: 'rgba(22,163,74,0.15)' } : {}}>
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-black mb-2" style={labelStyle}>{tx.successTitle}</h2>
              <p className="text-sm leading-relaxed mb-6" style={mutedStyle}>{tx.successSub}</p>
              <div className="rounded-xl p-4 mb-6 text-start" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#F7F8FA', border: '1px solid var(--auth-border)' }}>
                <p className="text-xs font-bold mb-2" style={mutedStyle}>{tx.requestSummary}</p>
                <div className="space-y-1.5">
                  {[
                    { label: tx.companyLabel, value: signupData.companyNameAr },
                    { label: tx.emailLabel, value: signupData.adminEmail },
                    ...(selectedPkg ? [{ label: tx.packageLabel, value: lang === 'en' ? (selectedPkg.name_en || selectedPkg.name_ar) : selectedPkg.name_ar, highlight: true }] : []),
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span style={mutedStyle}>{item.label}</span>
                      <span className={`font-black ${(item as any).highlight ? 'text-[#0F5C5C]' : ''}`} style={!(item as any).highlight ? labelStyle : {}}>{item.value}</span>
                    </div>
                  ))}
                  {(crDoc.url || vatDoc.url || paymentDoc.url) && (
                    <div className="flex justify-between text-xs pt-1 border-t border-[#E2E6EA] mt-1">
                      <span style={mutedStyle}>{lang === 'ar' ? 'المستندات المرفقة' : 'Attached Documents'}</span>
                      <span className="font-bold text-green-600">
                        {[crDoc.url && (lang === 'ar' ? 'السجل' : 'CR'), vatDoc.url && (lang === 'ar' ? 'الضريبة' : 'VAT'), paymentDoc.url && (lang === 'ar' ? 'السداد' : 'Payment')].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => { setMode('login'); setSignupStep(0); setError(''); setSuccess(''); }}
                className="w-full h-11 text-white rounded-xl text-sm font-black transition-all hover:brightness-110 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4)' }}>
                {tx.backToLoginBtn}
              </button>
            </div>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {mode === 'forgot' && (
            <div className="rounded-2xl border shadow-lg p-6" style={{ ...cardStyle, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div className="mb-5">
                <h1 className="text-xl font-black" style={labelStyle}>{tx.forgotTitle}</h1>
                <p className="text-sm mt-0.5" style={mutedStyle}>{tx.forgotSub}</p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-1.5" style={labelStyle}>{tx.email}</label>
                  <div className="relative">
                    <div className="absolute top-1/2 -translate-y-1/2 end-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,92,92,0.1)' }}>
                      <Mail size={13} style={{ color: '#0F5C5C' }} />
                    </div>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="example@company.sa" className={`${inputClass} pe-11`} style={inputStyle} />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full h-11 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 50%, #0A3D3D 100%)', boxShadow: '0 4px 16px rgba(15,92,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                  {loading ? <Loader2 size={16} className="animate-spin" /> : tx.sendReset}
                </button>
                <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                  className="w-full text-sm font-semibold transition-colors hover:text-[#0F5C5C]" style={mutedStyle}>
                  {tx.backToLogin}
                </button>
              </form>
            </div>
          )}

          {/* ── Contact Us Button ── */}
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="flex items-center gap-2 h-10 px-5 rounded-xl text-sm font-bold transition-all hover:scale-105 border"
              style={{
                background: isDark ? 'rgba(15,92,92,0.15)' : 'rgba(15,92,92,0.07)',
                borderColor: isDark ? 'rgba(15,92,92,0.4)' : 'rgba(15,92,92,0.2)',
                color: '#0F5C5C',
                boxShadow: '0 2px 8px rgba(15,92,92,0.1)',
              }}
            >
              <MessageSquare size={15} />
              <span>{tx.contactUs}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
